import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  User, BookOpen, Calendar, Trophy, LogOut, 
  FileText, Video, MessageCircle, Loader2, Mail, Sparkles, Brain, Zap, TrendingUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StudentDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [student, setStudent] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [batches, setBatches] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('studentToken');
    if (!token) {
      navigate('/student/login');
      return;
    }
    fetchStudentData();
  }, []);

  const fetchStudentData = async () => {
    try {
      const token = localStorage.getItem('studentToken');
      const headers = {
        'Authorization': `Bearer ${token}`
      };

      // Fetch student info
      const studentRes = await fetch(`${API_URL}/api/student/auth/me`, { headers });
      const studentData = await studentRes.json();
      setStudent(studentData);

      // Fetch enrollments
      const enrollmentsRes = await fetch(`${API_URL}/api/enrollments/my-enrollments`, { headers });
      const enrollmentsData = await enrollmentsRes.json();
      setEnrollments(enrollmentsData);

      // Fetch batches
      const batchesRes = await fetch(`${API_URL}/api/batches`);
      const batchesData = await batchesRes.json();
      setBatches(batchesData);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('studentToken');
    localStorage.removeItem('studentUser');
    navigate('/student/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="animate-spin text-red-600" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Dashboard Header */}
      <section className="bg-gradient-to-r from-red-600 to-red-700 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">Welcome back, {student?.full_name}!</h1>
              <p className="text-red-100">{student?.email}</p>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="bg-white text-red-600 hover:bg-red-50"
            >
              <LogOut className="mr-2" size={16} />
              Logout
            </Button>
          </div>
        </div>
      </section>

      {/* Dashboard Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Quick Stats */}
              <div className="grid md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-6 text-center">
                    <BookOpen className="mx-auto text-red-600 mb-3" size={32} />
                    <p className="text-3xl font-bold text-gray-900">{enrollments.length}</p>
                    <p className="text-gray-600">Enrolled Courses</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6 text-center">
                    <Trophy className="mx-auto text-yellow-600 mb-3" size={32} />
                    <p className="text-3xl font-bold text-gray-900">0</p>
                    <p className="text-gray-600">Tests Taken</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6 text-center">
                    <Calendar className="mx-auto text-green-600 mb-3" size={32} />
                    <p className="text-3xl font-bold text-gray-900">0</p>
                    <p className="text-gray-600">Classes Attended</p>
                  </CardContent>
                </Card>
              </div>

              {/* My Enrollments */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">My Enrolled Courses</CardTitle>
                  <CardDescription>Batches you are currently enrolled in</CardDescription>
                </CardHeader>
                <CardContent>
                  {enrollments.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <BookOpen className="mx-auto text-gray-400 mb-3" size={48} />
                      <p className="text-gray-600 mb-4">You haven't enrolled in any courses yet</p>
                      <Link to="/student/batches">
                        <Button className="bg-red-600 hover:bg-red-700">
                          Browse Batches
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {enrollments.map((enrollment) => {
                        const batch = batches.find(b => b.id === enrollment.batch_id);
                        return (
                          <Card key={enrollment.id} className="border-l-4 border-l-blue-600">
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between">
                                <div>
                                  <h3 className="font-bold text-lg">{batch?.name || 'Batch'}</h3>
                                  <p className="text-sm text-gray-600">{batch?.course_name}</p>
                                  <div className="flex gap-4 mt-2 text-sm text-gray-500">
                                    <span>📅 {batch?.timing}</span>
                                    <span>👨‍🏫 {batch?.instructor_name}</span>
                                  </div>
                                </div>
                                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                                  {enrollment.payment_status === 'completed' ? 'Active' : 'Pending'}
                                </span>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* AI Tools Section */}
              <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-red-50">
                <CardHeader>
                  <CardTitle className="text-2xl flex items-center">
                    <Sparkles className="mr-2 text-purple-600" size={24} />
                    AI-Powered Study Tools
                  </CardTitle>
                  <CardDescription>Smart tools to accelerate your preparation</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Link to="/student/ai-chatbot">
                    <Card className="hover:shadow-lg transition-shadow cursor-pointer border-purple-200">
                      <CardContent className="p-4 flex items-center">
                        <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mr-4">
                          <MessageCircle className="text-purple-600" size={24} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">🙏 Guruji AI</h3>
                          <p className="text-xs text-gray-600">Ask any doubt instantly</p>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>

                  <Link to="/student/ai-study-plan">
                    <Card className="hover:shadow-lg transition-shadow cursor-pointer border-red-200">
                      <CardContent className="p-4 flex items-center">
                        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mr-4">
                          <Brain className="text-red-600" size={24} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">Study Plan Generator</h3>
                          <p className="text-xs text-gray-600">Get personalized study roadmap</p>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>

                  <Link to="/student/ai-questions">
                    <Card className="hover:shadow-lg transition-shadow cursor-pointer border-green-200">
                      <CardContent className="p-4 flex items-center">
                        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mr-4">
                          <Zap className="text-green-600" size={24} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">Question Generator</h3>
                          <p className="text-xs text-gray-600">Practice with AI-generated MCQs</p>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Link to="/student/batches" className="block">
                    <Button className="w-full bg-red-600 hover:bg-red-700" variant="default">
                      <Calendar className="mr-2" size={16} />
                      View All Batches
                    </Button>
                  </Link>
                  <Link to="/student/tests" className="block">
                    <Button className="w-full" variant="outline">
                      <Trophy className="mr-2" size={16} />
                      Test Series
                    </Button>
                  </Link>
                  <Link to="/student/performance" className="block">
                    <Button className="w-full" variant="outline">
                      <TrendingUp className="mr-2" size={16} />
                      Performance Dashboard
                    </Button>
                  </Link>
                  <Link to="/student/live-classes" className="block">
                    <Button className="w-full" variant="outline">
                      <Video className="mr-2" size={16} />
                      Live Classes
                    </Button>
                  </Link>
                  <Link to="/resources" className="block">
                    <Button className="w-full" variant="outline">
                      <FileText className="mr-2" size={16} />
                      Study Materials
                    </Button>
                  </Link>
                  <Link to="/latest-updates" className="block">
                    <Button className="w-full" variant="outline">
                      <MessageCircle className="mr-2" size={16} />
                      Latest Updates
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              {/* Profile Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Profile</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex items-center text-gray-700">
                    <User className="mr-2 text-gray-400" size={16} />
                    <span className="font-medium">Name:</span>
                    <span className="ml-2">{student?.full_name}</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <Mail className="mr-2 text-gray-400" size={16} />
                    <span className="font-medium">Email:</span>
                    <span className="ml-2 text-xs">{student?.email}</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <span className="mr-2 text-gray-400">📱</span>
                    <span className="font-medium">Phone:</span>
                    <span className="ml-2">{student?.phone}</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-4"
                  >
                    Edit Profile
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StudentDashboard;
